import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail, formatDuration, formatHM } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { TimePicker } from "@/components/ui/time-picker";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  ResponsiveContainer, Tooltip, PieChart, Pie, Cell,
} from "recharts";
import { Download, Save, Trash2, FilePlus, Pencil } from "lucide-react";
import { MultiSelectFilter } from "@/components/MultiSelectFilter";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

const TEAL_PALETTE = ["#145c8c", "#00b5cc", "#0b2c47", "#3b82f6", "#0ea5e9", "#5eead4", "#1e3a8a", "#22d3ee"];

function isoDate(d) {
  const x = d instanceof Date ? d : new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfMonth(d) {
  const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x;
}
function endOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth() + 1, 0); x.setHours(0,0,0,0); return x;
}
function startOfWeekMon(d) {
  const x = new Date(d); x.setHours(0,0,0,0);
  const day = x.getDay(); const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff); return x;
}
function endOfWeekMon(d) {
  const s = startOfWeekMon(d); const e = new Date(s); e.setDate(e.getDate() + 6); return e;
}
function startOfQuarter(d) {
  const m = Math.floor(d.getMonth() / 3) * 3;
  return new Date(d.getFullYear(), m, 1);
}
function endOfQuarter(d) {
  const m = Math.floor(d.getMonth() / 3) * 3 + 2;
  return new Date(d.getFullYear(), m + 1, 0);
}

const DATE_SHORTCUTS = [
  { id: "today", label: "Today", get: () => { const t = new Date(); return [t, t]; } },
  { id: "this-week", label: "This week", get: () => { const t = new Date(); return [startOfWeekMon(t), endOfWeekMon(t)]; } },
  { id: "last-week", label: "Last week", get: () => { const t = new Date(); t.setDate(t.getDate() - 7); return [startOfWeekMon(t), endOfWeekMon(t)]; } },
  { id: "this-month", label: "This month", get: () => { const t = new Date(); return [startOfMonth(t), endOfMonth(t)]; } },
  { id: "last-month", label: "Last month", get: () => {
      // Anchor to the 1st BEFORE moving back a month; otherwise on days
      // like Jul 31 → setMonth(-1) targets Jun 31 and JS rolls that forward
      // into July again, so "Last month" resolves to the same range as
      // "This month".
      const t = new Date(); t.setDate(1); t.setMonth(t.getMonth() - 1);
      return [startOfMonth(t), endOfMonth(t)];
    } },
  { id: "this-quarter", label: "This quarter", get: () => { const t = new Date(); return [startOfQuarter(t), endOfQuarter(t)]; } },
  { id: "last-quarter", label: "Last quarter", get: () => {
      // Same day-overflow guard as Last month — anchor to the 1st first.
      const t = new Date(); t.setDate(1); t.setMonth(t.getMonth() - 3);
      return [startOfQuarter(t), endOfQuarter(t)];
    } },
  { id: "this-year", label: "This year", get: () => { const t = new Date(); return [new Date(t.getFullYear(), 0, 1), new Date(t.getFullYear(), 11, 31)]; } },
  { id: "last-year", label: "Last year", get: () => { const t = new Date(); return [new Date(t.getFullYear() - 1, 0, 1), new Date(t.getFullYear() - 1, 11, 31)]; } },
];

export default function ReportsPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [activities, setActivities] = useState([]);
  const [tags, setTags] = useState([]);
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState(() => {
    const today = new Date();
    const start = startOfWeekMon(today);
    const end = endOfWeekMon(today);
    return {
      start_date: isoDate(start),
      end_date: isoDate(end),
      user_ids: [],
      client_ids: [],
      activity_ids: [],
      tag_ids: [],
      description: "",
      include_leave: false,
    };
  });
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [editEntry, setEditEntry] = useState(null);
  const isAdmin = user?.role === "admin";
  // Ref to the entire Summary tab body — snapshotted via html2canvas for the
  // "Export as PDF" that includes the actual chart graphs.
  const summaryRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const loadMeta = useCallback(async () => {
    try {
      const [c, a, m, tpl, tg] = await Promise.all([
        api.get("/clients"),
        api.get("/activities"),
        isAdmin ? api.get("/users") : Promise.resolve({ data: [] }),
        api.get("/report-templates"),
        api.get("/tags"),
      ]);
      setClients(c.data);
      setActivities(a.data);
      setMembers(m.data);
      setTemplates(tpl.data);
      setTags(tg.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [isAdmin]);

  const params = useMemo(() => {
    // Use local-midnight boundaries: appending "T00:00:00" (no "Z") parses
    // as LOCAL time so entries that live in the user's Monday-morning still
    // fall within the filter even when they cross into the previous UTC day
    // (e.g. Sydney AEST 07:00 Mon → Sun 21:00 UTC).
    const p = {
      start_date: new Date(filters.start_date + "T00:00:00").toISOString(),
      end_date: new Date(filters.end_date + "T23:59:59").toISOString(),
      scope: isAdmin ? "all" : "self",
      tz_offset: -new Date().getTimezoneOffset(), // minutes east of UTC
    };
    if (filters.user_ids?.length) p.user_ids = filters.user_ids.join(",");
    if (filters.client_ids?.length) p.client_ids = filters.client_ids.join(",");
    if (filters.activity_ids?.length) p.activity_ids = filters.activity_ids.join(",");
    if (filters.tag_ids?.length) p.tag_ids = filters.tag_ids.join(",");
    if (filters.description) p.description = filters.description;
    p.include_leave = filters.include_leave ? "true" : "false";
    return p;
  }, [filters, isAdmin]);

  const loadData = useCallback(async () => {
    try {
      const [sumRes, entRes] = await Promise.all([
        api.get("/reports/summary", { params }),
        api.get("/time-entries", { params }),
      ]);
      setSummary(sumRes.data);
      setEntries(entRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [params]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadData(); }, [loadData]);

  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
  const activityMap = Object.fromEntries(activities.map((a) => [a.id, a]));
  const memberMap = Object.fromEntries(members.map((m) => [m.id, m]));

  // Leave entries are bucketed by the backend under `leave:<type>` keys; map
  // them to a readable name + leave colour and flag them so the pie can render
  // with the striped fill.
  const LEAVE_LABELS = { annual: "Annual Leave", personal: "Personal Leave", sick: "Sick Leave" };
  const LEAVE_COLORS = { annual: "#0ea5e9", personal: "#8b5cf6", sick: "#ef4444" };

  const mapLeaveOrFallback = (id, fallback) => {
    if (typeof id === "string" && id.startsWith("leave:")) {
      const t = id.slice(6);
      return { name: LEAVE_LABELS[t] || "Leave", color: LEAVE_COLORS[t] || "#64748b", isLeave: true };
    }
    return fallback();
  };

  const byClientChart = (summary?.by_client || []).map((b) => ({
    ...mapLeaveOrFallback(b.client_id, () => ({
      name: b.client_id === "none" ? "No client" : clientMap[b.client_id]?.name || "Unknown",
      color: b.client_id === "none" ? "#94a3b8" : clientMap[b.client_id]?.color || "#145c8c",
      isLeave: false,
    })),
    seconds: b.seconds,
    hours: +(b.seconds / 3600).toFixed(2),
  })).sort((a, b) => b.seconds - a.seconds);

  const byActivityChart = (summary?.by_activity || []).map((b) => ({
    ...mapLeaveOrFallback(b.activity_id, () => ({
      name: b.activity_id === "none" ? "No activity" : activityMap[b.activity_id]?.name || "Unknown",
      color: b.activity_id === "none" ? "#94a3b8" : activityMap[b.activity_id]?.color || "#00b5cc",
      isLeave: false,
    })),
    seconds: b.seconds,
    hours: +(b.seconds / 3600).toFixed(2),
  })).sort((a, b) => b.seconds - a.seconds);

  const tagMap = Object.fromEntries(tags.map((t) => [t.id, t]));
  const byTagChart = (summary?.by_tag || []).map((b) => ({
    ...mapLeaveOrFallback(b.tag_id, () => ({
      name: b.tag_id === "none" ? "Uncategorised" : tagMap[b.tag_id]?.name || "Unknown",
      color: b.tag_id === "none" ? "#94a3b8" : tagMap[b.tag_id]?.color || "#9333ea",
      isLeave: false,
    })),
    seconds: b.seconds,
    hours: +(b.seconds / 3600).toFixed(2),
  })).sort((a, b) => b.seconds - a.seconds);

  // By Team — average daily hours per team member (work only). Backend
  // returns per-user `avg_daily_seconds` using each user's own working_days
  // preference so this metric is fair for part-timers.
  const byTeamChart = (summary?.by_user || []).map((u) => {
    const m = memberMap[u.user_id];
    return {
      name: m?.name || m?.email || "Unknown",
      color: m?.color || "#145c8c",
      isLeave: false,
      seconds: u.avg_daily_seconds,
    };
  }).sort((a, b) => b.seconds - a.seconds);

  // Build by-day chart filling in zeros for every date in the selected range
  // and splitting leave vs work hours so they can be stacked & styled separately.
  const byDayChart = useMemo(() => {
    const map = new Map(); // date → { work, leave }
    // Seed every date in [start_date, end_date]
    try {
      const s = new Date(filters.start_date + "T00:00:00");
      const e = new Date(filters.end_date + "T00:00:00");
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        map.set(isoDate(d), { work: 0, leave: 0 });
      }
    } catch { /* invalid date — fall through */ }
    // Accumulate from entries (so we can split leave/work)
    for (const en of entries) {
      if (!en.end) continue;
      try {
        const day = isoDate(typeof en.start === "string" ? new Date(en.start) : en.start);
        if (!map.has(day)) map.set(day, { work: 0, leave: 0 });
        const bucket = map.get(day);
        if (en.leave_type) bucket.leave += en.duration_seconds || 0;
        else bucket.work += en.duration_seconds || 0;
      } catch { /* ignore */ }
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, v]) => ({
        date,
        label: new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }),
        workHours: +(v.work / 3600).toFixed(2),
        leaveHours: +(v.leave / 3600).toFixed(2),
        hours: +((v.work + v.leave) / 3600).toFixed(2),
      }));
  }, [entries, filters.start_date, filters.end_date]);

  const exportSummaryVisualPDF = async () => {
    if (!summaryRef.current) return;
    setExporting(true);
    try {
      // html2canvas snapshots the DOM at 2× scale for a crisp result.
      // scrollX/Y = 0 makes it respect the actual layout regardless of
      // the outer scroll position.
      const canvas = await html2canvas(summaryRef.current, {
        scale: 1.5,
        backgroundColor: "#ffffff",
        useCORS: true,
        scrollX: 0,
        scrollY: -window.scrollY,
        windowWidth: summaryRef.current.scrollWidth,
      });
      const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 32;
      const contentW = pageW - margin * 2;

      // Header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(11, 44, 71);
      doc.text("Dreamtime Time Log — Summary", margin, 44);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Range: ${filters.start_date} → ${filters.end_date}`, margin, 60);
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, 72);

      // Filters summary — surfaces the applied filters at the top so the PDF
      // is self-documenting.
      const nameFor = (id, map, noneLabel) =>
        id === "__none__" ? noneLabel : map[id]?.name || tags.find((t) => t.id === id)?.name || id;
      const activeFilters = [];
      if (isAdmin && filters.user_ids.length) {
        activeFilters.push(`Members: ${filters.user_ids.map((id) => memberMap[id]?.name || id).join(", ")}`);
      }
      if (filters.client_ids.length) {
        activeFilters.push(`Clients: ${filters.client_ids.map((id) => nameFor(id, clientMap, "No client")).join(", ")}`);
      }
      if (filters.tag_ids.length) {
        const tagName = (id) => id === "__none__" ? "No category" : (tags.find((t) => t.id === id)?.name || id);
        activeFilters.push(`Categories: ${filters.tag_ids.map(tagName).join(", ")}`);
      }
      if (filters.activity_ids.length) {
        activeFilters.push(`Activities: ${filters.activity_ids.map((id) => nameFor(id, activityMap, "No activity")).join(", ")}`);
      }
      if (filters.description) activeFilters.push(`Description contains: "${filters.description}"`);
      if (!filters.include_leave) activeFilters.push("Leave entries hidden");
      if (activeFilters.length > 0) {
        doc.text(`Filters: ${activeFilters.join(" · ")}`, margin, 84);
      }

      // Image: scale to page width, and paginate vertically if it overflows.
      const imgW = contentW;
      const imgH = (canvas.height / canvas.width) * imgW;
      const startY = activeFilters.length > 0 ? 96 : 84;
      const availPerPage = pageH - startY - margin;
      const imgData = canvas.toDataURL("image/jpeg", 0.85);

      if (imgH <= availPerPage) {
        doc.addImage(imgData, "JPEG", margin, startY, imgW, imgH);
      } else {
        // Multi-page: draw the same image but shift its Y off-screen per page.
        // Simple approach — draw full image, use jsPDF page clipping. Each
        // page shows a `availPerPage`-tall slice starting at yOffset.
        let yOffset = 0;
        let firstPage = true;
        while (yOffset < imgH) {
          if (!firstPage) doc.addPage();
          firstPage = false;
          const drawY = (firstPage ? startY : margin) - yOffset;
          doc.addImage(imgData, "JPEG", margin, drawY, imgW, imgH);
          // Cover header on subsequent pages
          if (yOffset > 0) {
            doc.setFillColor(255, 255, 255);
            doc.rect(0, 0, pageW, margin, "F");
          }
          yOffset += availPerPage;
        }
      }

      doc.save(`dreamtime-summary-${filters.start_date}_${filters.end_date}.pdf`);
    } catch (e) {
      toast.error("Failed to export PDF: " + (e.message || "unknown error"));
    } finally {
      setExporting(false);
    }
  };

  const exportPDF = (mode = "summary") => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(11, 44, 71);
    doc.text("Dreamtime Time Log — Report", 40, 50);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Range: ${filters.start_date} → ${filters.end_date}`, 40, 70);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 84);

    if (mode === "summary" && summary) {
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text(`Total: ${formatDuration(summary.total_seconds)}`, 40, 110);
      doc.text(`Avg Daily: ${formatDuration(summary.avg_daily_seconds)}`, 200, 110);
      doc.text(`Entries: ${summary.entry_count}`, 360, 110);

      autoTable(doc, {
        startY: 130,
        head: [["Date", "Duration"]],
        body: byDayChart.map((d) => [d.date, formatHM(d.hours * 3600)]),
        styles: { fontSize: 10 },
        headStyles: { fillColor: [20, 92, 140], textColor: 255 },
      });

      const lastY = doc.lastAutoTable.finalY + 20;
      autoTable(doc, {
        startY: lastY,
        head: [["Client", "Duration"]],
        body: byClientChart.map((c) => [c.name, formatHM(c.seconds)]),
        styles: { fontSize: 10 },
        headStyles: { fillColor: [20, 92, 140], textColor: 255 },
      });

      const lastY2 = doc.lastAutoTable.finalY + 20;
      autoTable(doc, {
        startY: lastY2,
        head: [["Activity", "Duration"]],
        body: byActivityChart.map((c) => [c.name, formatHM(c.seconds)]),
        styles: { fontSize: 10 },
        headStyles: { fillColor: [0, 181, 204], textColor: 255 },
      });
    } else {
      // detailed
      autoTable(doc, {
        startY: 110,
        head: [["Date", "Member", "Client", "Activity", "Description", "Start", "End", "Duration"]],
        body: entries.map((e) => {
          const s = new Date(e.start);
          const en = e.end ? new Date(e.end) : null;
          const eClientIds = Array.isArray(e.client_ids) && e.client_ids.length
            ? e.client_ids
            : (e.client_id ? [e.client_id] : []);
          const clientNames = eClientIds.map((id) => clientMap[id]?.name).filter(Boolean).join(", ");
          return [
            s.toLocaleDateString(),
            memberMap[e.user_id]?.name || (e.user_id === user.id ? user.name : ""),
            clientNames,
            activityMap[e.activity_id]?.name || "",
            e.description || "",
            s.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            en ? en.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—",
            formatDuration(e.duration_seconds),
          ];
        }),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [20, 92, 140], textColor: 255 },
      });
    }

    doc.save(`dreamtime-${mode}-${filters.start_date}_${filters.end_date}.pdf`);
  };

  const saveTemplate = async () => {
    if (!tplName.trim()) return;
    try {
      await api.post("/report-templates", { name: tplName.trim(), config: filters });
      setSaveOpen(false);
      setTplName("");
      loadMeta();
      toast.success("Template saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const applyTemplate = (tpl) => {
    // Templates saved before the multi-select migration use singular keys
    // (e.g. `user_id: "all"` | id | "__none__"). Normalise to the new
    // array-based shape so old templates still work.
    const cfg = { ...tpl.config };
    const migrate = (singular, plural) => {
      if (Array.isArray(cfg[plural])) return; // already migrated
      const v = cfg[singular];
      if (!v || v === "all") cfg[plural] = [];
      else cfg[plural] = [v];
      delete cfg[singular];
    };
    migrate("user_id", "user_ids");
    migrate("client_id", "client_ids");
    migrate("activity_id", "activity_ids");
    migrate("tag_id", "tag_ids");
    setFilters(cfg);
    toast.success(`Loaded "${tpl.name}"`);
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    await api.delete(`/report-templates/${tpl.id}`);
    loadMeta();
  };

  const deleteEntry = async (entry) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/time-entries/${entry.id}`);
      toast.success("Entry deleted");
      loadData();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const saveEntry = async (data) => {
    if (!editEntry) return;
    try {
      await api.put(`/time-entries/${editEntry.id}`, data);
      toast.success("Entry updated");
      setEditEntry(null);
      loadData();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Top bar */}
      <div className="h-16 px-6 border-b border-slate-200 flex items-center bg-white">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>Reports</h1>
        <div className="ml-auto flex items-center gap-2">
          <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="h-9" data-testid="save-template-button">
                <Save className="h-4 w-4 mr-1" /> Save as template
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Save Report Template</DialogTitle>
              </DialogHeader>
              <Input value={tplName} onChange={(e) => setTplName(e.target.value)}
                     placeholder="e.g. All tasks by date, grouped by Client"
                     data-testid="template-name-input" />
              <DialogFooter>
                <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
                <Button onClick={saveTemplate} className="text-white" style={{ background: "#145c8c" }}
                        data-testid="template-save-confirm">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="p-6 space-y-6 overflow-y-auto">
        {/* Filter bar */}
        <div className="bg-white border border-slate-200 rounded-xl p-4"
             data-testid="filters-bar">
          {/* Date shortcuts */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="date-shortcuts">
            {DATE_SHORTCUTS.map((s) => {
              const [a, b] = s.get();
              const active = filters.start_date === isoDate(a) && filters.end_date === isoDate(b);
              return (
                <button
                  key={s.id}
                  onClick={() => setFilters({ ...filters, start_date: isoDate(a), end_date: isoDate(b) })}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    active ? "bg-[#145c8c] text-white border-[#145c8c]"
                           : "bg-white text-slate-600 border-slate-200 hover:border-[#145c8c] hover:text-[#145c8c]"
                  }`}
                  data-testid={`date-shortcut-${s.id}`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs text-slate-500">From</Label>
            <DatePicker value={filters.start_date}
                        onChange={(v) => setFilters({ ...filters, start_date: v })}
                        className="h-9 mt-1 w-full" testId="filter-start-date" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">To</Label>
            <DatePicker value={filters.end_date}
                        onChange={(v) => setFilters({ ...filters, end_date: v })}
                        className="h-9 mt-1 w-full" testId="filter-end-date" />
          </div>
          {isAdmin && (
            <div>
              <Label className="text-xs text-slate-500">Member</Label>
              <MultiSelectFilter
                options={members.map((m) => ({ id: m.id, name: m.name || m.email, color: m.color }))}
                value={filters.user_ids}
                onChange={(v) => setFilters({ ...filters, user_ids: v })}
                allLabel="All members"
                testId="filter-member"
              />
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-500">Client</Label>
            <MultiSelectFilter
              options={[
                { id: "__none__", name: "(No client)", color: "#94a3b8" },
                ...clients.map((c) => ({ id: c.id, name: c.name, color: c.color })),
              ]}
              value={filters.client_ids}
              onChange={(v) => setFilters({ ...filters, client_ids: v })}
              allLabel="All clients"
              testId="filter-client"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Category</Label>
            <MultiSelectFilter
              options={[
                { id: "__none__", name: "(No category)", color: "#94a3b8" },
                ...tags.map((t) => ({ id: t.id, name: t.name, color: t.color })),
              ]}
              value={filters.tag_ids}
              onChange={(v) => setFilters({ ...filters, tag_ids: v })}
              allLabel="All categories"
              testId="filter-tag"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Activity</Label>
            <MultiSelectFilter
              options={[
                { id: "__none__", name: "(No activity)", color: "#94a3b8" },
                ...activities.map((a) => ({ id: a.id, name: a.name, color: a.color })),
              ]}
              value={filters.activity_ids}
              onChange={(v) => setFilters({ ...filters, activity_ids: v })}
              allLabel="All activities"
              testId="filter-activity"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Description</Label>
            <Input value={filters.description}
                   onChange={(e) => setFilters({ ...filters, description: e.target.value })}
                   placeholder="search…" className="h-9 mt-1" data-testid="filter-description" />
          </div>
          <div className="col-span-2 md:col-span-6 flex items-center pt-1">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox"
                     checked={filters.include_leave}
                     onChange={(e) => setFilters({ ...filters, include_leave: e.target.checked })}
                     data-testid="filter-include-leave" />
              Include leave entries
            </label>
          </div>
          </div>
        </div>

        <Tabs defaultValue="summary">
          <TabsList className="bg-slate-100" data-testid="report-tabs">
            <TabsTrigger value="summary" data-testid="report-tab-summary">Summary</TabsTrigger>
            <TabsTrigger value="detailed" data-testid="report-tab-detailed">Detailed</TabsTrigger>
            <TabsTrigger value="team" data-testid="report-tab-team">Team</TabsTrigger>
            <TabsTrigger value="my" data-testid="report-tab-my">My Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="mt-5 space-y-6">
            {/* Export bar — top of the tab so it's always reachable */}
            <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="text-sm text-slate-600">
                Summary for <span className="font-medium text-slate-800">{filters.start_date}</span> → <span className="font-medium text-slate-800">{filters.end_date}</span>
                {summary && <span className="text-slate-400"> · {formatDuration(summary.total_seconds)} total · {summary.entry_count} entries</span>}
              </div>
              <Button variant="outline" className="h-8"
                      disabled={exporting}
                      onClick={exportSummaryVisualPDF}
                      data-testid="export-pdf-summary">
                <Download className="h-4 w-4 mr-1" />
                {exporting ? "Exporting…" : "Export as PDF"}
              </Button>
            </div>

            <div ref={summaryRef} className="space-y-6" data-testid="summary-report-content">
            {/* Left: Activity (long list gets its own column).
                Right: Client / Category / Team stacked. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              <Distribution title="By Activity" data={byActivityChart} testid="dist-activity" />
              <div className="flex flex-col gap-4">
                <Distribution title="By Client" data={byClientChart} testid="dist-client" />
                <Distribution title="By Category" data={byTagChart} testid="dist-tag" />
                <Distribution title="Team (avg daily hours)" data={byTeamChart} testid="dist-team" />
              </div>
            </div>

            </div>
          </TabsContent>

          <TabsContent value="detailed" className="mt-5">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="text-sm font-medium text-slate-700">Detailed entries ({entries.length})</div>
                <Button variant="outline" className="h-8" onClick={() => exportPDF("detailed")}
                        data-testid="export-pdf-detailed">
                  <Download className="h-4 w-4 mr-1" /> Export PDF
                </Button>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-2">Date</th>
                      {isAdmin && <th className="text-left px-4 py-2">Member</th>}
                      <th className="text-left px-4 py-2">Description</th>
                      <th className="text-left px-4 py-2">Client</th>
                      <th className="text-left px-4 py-2">Category</th>
                      <th className="text-left px-4 py-2">Activity</th>
                      <th className="text-left px-4 py-2">Start</th>
                      <th className="text-left px-4 py-2">End</th>
                      <th className="text-right px-4 py-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => {
                      const s = new Date(e.start);
                      const en = e.end ? new Date(e.end) : null;
                      const eClientIds = Array.isArray(e.client_ids) && e.client_ids.length
                        ? e.client_ids
                        : (e.client_id ? [e.client_id] : []);
                      const eClients = eClientIds.map((id) => clientMap[id]).filter(Boolean);
                      const a = activityMap[e.activity_id];
                      return (
                        <tr key={e.id} className="border-t border-slate-100 group" data-testid={`detail-row-${e.id}`}>
                          <td className="px-4 py-2 text-slate-600">{s.toLocaleDateString()}</td>
                          {isAdmin && <td className="px-4 py-2 text-slate-600">{memberMap[e.user_id]?.name || "—"}</td>}
                          <td className="px-4 py-2 font-medium">
                            {e.leave_type && (
                              <span className="text-[9px] uppercase tracking-widest text-slate-500 mr-1">
                                {e.leave_type} leave ·
                              </span>
                            )}
                            {e.description || <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-4 py-2">
                            {eClients.length === 0 ? "—" : (
                              <span className="inline-flex items-center gap-1.5 flex-wrap">
                                {eClients.map((c, idx) => (
                                  <span key={c.id} className="inline-flex items-center gap-1">
                                    <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
                                    {c.name}
                                    {idx < eClients.length - 1 && <span className="text-slate-300">,</span>}
                                  </span>
                                ))}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            {(() => {
                              const eTags = (e.tag_ids || []).map((tid) => tagMap[tid]).filter(Boolean);
                              return eTags.length === 0 ? "—" : (
                                <span className="inline-flex items-center gap-1.5 flex-wrap">
                                  {eTags.map((t) => (
                                    <span key={t.id}
                                          className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium leading-[1.15]"
                                          style={{ background: `${t.color}1a`, color: t.color }}>
                                      {t.name}
                                    </span>
                                  ))}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-2">{a ? <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{background:a.color}} />{a.name}</span> : "—"}</td>
                          <td className="px-4 py-2 timer-font text-xs text-slate-500">{s.toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</td>
                          <td className="px-4 py-2 timer-font text-xs text-slate-500">{en ? en.toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"}) : "—"}</td>
                          <td className="px-4 py-2 text-right timer-font">{formatDuration(e.duration_seconds)}</td>
                        </tr>
                      );
                    })}
                    {entries.length === 0 && (
                      <tr><td colSpan={isAdmin ? 9 : 8} className="text-center py-10 text-slate-400">No entries match the filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="team" className="mt-5">
            <TeamReport
              filters={filters}
              params={params}
              clientMap={clientMap}
              activityMap={activityMap}
              tagMap={Object.fromEntries(tags.map((t) => [t.id, t]))}
              members={members}
              isAdmin={isAdmin}
            />
          </TabsContent>

          <TabsContent value="my" className="mt-5">
            <div className="bg-white border border-slate-200 rounded-xl">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center">
                <div className="text-sm font-medium text-slate-700">My saved report templates</div>
              </div>
              {templates.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-sm">
                  <FilePlus className="h-6 w-6 mx-auto mb-2 text-slate-300" />
                  No saved templates yet. Configure filters above and click &quot;Save as template&quot;.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2">Name</th>
                      <th className="text-left px-4 py-2">Range</th>
                      <th className="text-right px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.map((t) => (
                      <tr key={t.id} className="border-t border-slate-100" data-testid={`template-row-${t.id}`}>
                        <td className="px-4 py-3 font-medium">{t.name}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">
                          {t.config?.start_date} → {t.config?.end_date}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="outline" className="h-8 mr-2" onClick={() => applyTemplate(t)}
                                  data-testid={`template-apply-${t.id}`}>Load</Button>
                          <button onClick={() => deleteTemplate(t)}
                                  className="p-1.5 hover:bg-red-50 rounded-md text-red-500"
                                  data-testid={`template-delete-${t.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Inline edit entry dialog */}
      <Dialog open={!!editEntry} onOpenChange={(v) => !v && setEditEntry(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit time entry</DialogTitle>
          </DialogHeader>
          {editEntry && (
            <EntryEditForm
              entry={editEntry}
              clients={clients}
              activities={activities}
              onCancel={() => setEditEntry(null)}
              onSave={saveEntry}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EntryEditForm({ entry, clients, activities, onCancel, onSave }) {
  const [description, setDescription] = useState(entry.description || "");
  const seedClientIds = Array.isArray(entry.client_ids) && entry.client_ids.length
    ? entry.client_ids
    : (entry.client_id ? [entry.client_id] : []);
  const [clientIds, setClientIds] = useState(seedClientIds);
  const [activityId, setActivityId] = useState(entry.activity_id || null);
  const startDt = new Date(entry.start);
  const endDt = entry.end ? new Date(entry.end) : new Date(entry.start);
  const [dateStr, setDateStr] = useState(startDt.toISOString().slice(0, 10));
  const [startStr, setStartStr] = useState(`${String(startDt.getHours()).padStart(2,"0")}:${String(startDt.getMinutes()).padStart(2,"0")}`);
  const [endStr, setEndStr] = useState(`${String(endDt.getHours()).padStart(2,"0")}:${String(endDt.getMinutes()).padStart(2,"0")}`);

  const toggleClient = (id) => {
    setClientIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const submit = () => {
    const [y, mo, da] = dateStr.split("-").map(Number);
    const [sh, sm] = startStr.split(":").map(Number);
    const [eh, em] = endStr.split(":").map(Number);
    const s = new Date(y, mo - 1, da, sh, sm, 0, 0);
    const e = new Date(y, mo - 1, da, eh, em, 0, 0);
    if (e <= s) { toast.error("End must be after start"); return; }
    onSave({
      description, client_ids: clientIds, activity_id: activityId,
      leave_type: entry.leave_type || null,
      start: s.toISOString(), end: e.toISOString(),
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs text-slate-500">Description</Label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)}
               className="mt-1" data-testid="edit-description" />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Clients</Label>
        <div className="mt-1 flex flex-wrap gap-1.5 p-2 border border-slate-200 rounded-md max-h-32 overflow-y-auto"
             data-testid="edit-client">
          {clients.length === 0 ? (
            <span className="text-xs text-slate-400">No clients yet</span>
          ) : clients.map((c) => {
            const on = clientIds.includes(c.id);
            return (
              <button key={c.id} type="button" onClick={() => toggleClient(c.id)}
                      className={`px-2 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                        on ? "border-transparent" : "border-slate-200 text-slate-500 hover:border-slate-300"
                      }`}
                      style={on ? { background: `${c.color}1a`, color: c.color } : {}}
                      data-testid={`edit-client-${c.id}`}>
                {c.name}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Select value={activityId || "none"} onValueChange={(v) => setActivityId(v === "none" ? null : v)}>
          <SelectTrigger data-testid="edit-activity"><SelectValue placeholder="Activity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— None —</SelectItem>
            {activities.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs text-slate-500">Date</Label>
          <DatePicker value={dateStr} onChange={setDateStr}
                      className="mt-1 w-full" testId="edit-date" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Start</Label>
          <TimePicker value={startStr} onChange={setStartStr}
                      className="mt-1 w-full" testId="edit-start" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">End</Label>
          <TimePicker value={endStr} onChange={setEndStr}
                      className="mt-1 w-full" testId="edit-end" />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={submit} className="text-white" style={{ background: "#145c8c" }}
                data-testid="edit-save">Save</Button>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className="mt-2 text-3xl font-semibold tracking-tight tabular-nums" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

function Distribution({ title, data, testid }) {
  // Sort descending by seconds and compute a max so every bar shares the same
  // horizontal scale within a chart.
  const sorted = [...data].sort((a, b) => b.seconds - a.seconds);
  const max = sorted.reduce((m, d) => Math.max(m, d.seconds), 0) || 1;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col"
         data-testid={testid}>
      <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      {sorted.length === 0 ? (
        <div className="py-8 text-center text-slate-400 text-sm">No data</div>
      ) : (
        <div className="flex-1 flex flex-col gap-2">
          {sorted.map((d, i) => {
            const pct = Math.max(2, (d.seconds / max) * 100);
            const barBg = d.isLeave
              ? `repeating-linear-gradient(45deg, ${d.color}55 0 4px, transparent 4px 8px), ${d.color}`
              : d.color;
            return (
              <div key={i} className="flex items-center gap-2 text-xs"
                   data-testid={`${testid}-row-${i}`}>
                {/* Left: name — wider to reduce truncation, smaller text. */}
                <div className="w-44 shrink-0 flex items-center gap-1.5 justify-end text-right">
                  <span className="truncate text-slate-700" title={d.name}>{d.name}</span>
                  {d.isLeave ? (
                    <span className="h-2.5 w-2.5 rounded-sm border shrink-0"
                          style={{ borderColor: d.color,
                                   backgroundImage: `repeating-linear-gradient(45deg, ${d.color}55 0 2px, transparent 2px 4px)` }} />
                  ) : (
                    <span className="h-2.5 w-2.5 rounded-full shrink-0"
                          style={{ background: d.color }} />
                  )}
                </div>
                {/* Center divider */}
                <div className="w-px self-stretch bg-slate-200 shrink-0" />
                {/* Right: bar with hours label inside */}
                <div className="flex-1 min-w-0 h-6 relative">
                  <div className="absolute inset-y-0 left-0 rounded-md flex items-center justify-end pr-2 shadow-sm"
                       style={{
                         width: `${pct}%`,
                         minWidth: "2.75rem",  // ensures the label fits even on tiny slices
                         background: barBg,
                       }}>
                    <span className="text-white text-[11px] font-semibold timer-font tabular-nums whitespace-nowrap"
                          style={{ textShadow: "0 1px 1px rgba(0,0,0,0.15)" }}>
                      {formatHM(d.seconds)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// TEAM REPORT — per-member daily totals (with 7h tick / leave grey) and a
// member's top-10 entries (description + client + category + activity).
// ============================================================================
function TeamReport({ filters, params, clientMap, activityMap, tagMap, members, isAdmin }) {
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Re-use the exact same params object as the Summary/Detailed tabs so
        // Client / Category / Activity / Description / Member filters all
        // affect the Team view too. `params` already builds local-midnight
        // ISO boundaries and passes __none__ sentinels correctly.
        const { data } = await api.get("/time-entries", { params });
        if (!cancelled) setEntries(data || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [params]);

  // Restrict the displayed member list when specific members are picked in
  // the filter — hides everyone else.
  const visibleMembers = React.useMemo(() => (
    filters.user_ids?.length
      ? members.filter((m) => filters.user_ids.includes(m.id))
      : members
  ), [members, filters.user_ids]);

  // Days array Mon..end in the filter range
  const days = React.useMemo(() => {
    const out = [];
    const s = new Date(filters.start_date + "T00:00:00");
    const e = new Date(filters.end_date + "T00:00:00");
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      out.push(new Date(d));
    }
    return out;
  }, [filters.start_date, filters.end_date]);

  // Aggregate entries per member per day (only for the visible members set)
  const byMember = React.useMemo(() => {
    const map = new Map();
    for (const m of visibleMembers) map.set(m.id, { daily: new Map(), entries: [] });
    for (const en of entries) {
      if (!en.end) continue;
      if (!map.has(en.user_id)) continue;  // skip entries for hidden members
      const bucket = map.get(en.user_id);
      const d = new Date(en.start);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const dayBucket = bucket.daily.get(key) || { work: 0, leave: 0, leaveType: null };
      if (en.leave_type) {
        dayBucket.leave += en.duration_seconds || 0;
        dayBucket.leaveType = dayBucket.leaveType || en.leave_type;
      } else {
        dayBucket.work += en.duration_seconds || 0;
      }
      bucket.daily.set(key, dayBucket);
      bucket.entries.push(en);
    }
    return map;
  }, [entries, members]);

  // Top-10 entries grouped per member by (desc + all client_ids + activity + first category).
  // Multi-client entries stay together as one group and their full duration is
  // credited to the group (this is per-task, not per-client — so no splitting).
  const topByMember = React.useMemo(() => {
    const out = new Map();
    for (const [uid, b] of byMember.entries()) {
      const groups = new Map();
      for (const en of b.entries) {
        if (en.leave_type) continue;
        const cids = (en.client_ids && en.client_ids.length)
          ? [...en.client_ids].sort()
          : (en.client_id ? [en.client_id] : []);
        const cidsKey = cids.join(",");
        const aid = en.activity_id || "";
        const tid = (en.tag_ids && en.tag_ids[0]) || "";
        const key = `${(en.description || "").trim().toLowerCase()}::${cidsKey}::${aid}::${tid}`;
        const cur = groups.get(key) || {
          description: en.description || "(no description)",
          clientIds: cids, activityId: aid, tagId: tid, seconds: 0,
        };
        cur.seconds += en.duration_seconds || 0;
        groups.set(key, cur);
      }
      const arr = Array.from(groups.values()).sort((a, b) => b.seconds - a.seconds).slice(0, 10);
      out.set(uid, arr);
    }
    return out;
  }, [byMember]);

  // Per-member distribution pies — sum seconds grouped by first client / first
  // category / activity. Leave entries are excluded so pies represent tracked
  // work only.
  const distByMember = React.useMemo(() => {
    const out = new Map();
    const toData = (map, lookup, defaultColor) => (
      Array.from(map.entries()).map(([id, sec]) => {
        if (id === "__none__") {
          return { id, name: "None", color: "#94a3b8", seconds: sec };
        }
        const item = lookup[id];
        return { id, name: item?.name || "Unknown", color: item?.color || defaultColor, seconds: sec };
      }).sort((a, b) => b.seconds - a.seconds)
    );
    for (const [uid, b] of byMember.entries()) {
      const byClient = new Map();
      const byCat = new Map();
      const byAct = new Map();
      for (const en of b.entries) {
        if (en.leave_type) continue;
        const sec = en.duration_seconds || 0;
        // Multi-client entries: split the tracked time evenly across every
        // assigned client so the pie totals match the entry's real duration
        // (matches the backend /reports/summary by_client behaviour).
        const cids = (en.client_ids && en.client_ids.length)
          ? en.client_ids
          : (en.client_id ? [en.client_id] : []);
        if (cids.length === 0) {
          byClient.set("__none__", (byClient.get("__none__") || 0) + sec);
        } else {
          const share = sec / cids.length;
          for (const cid of cids) {
            byClient.set(cid, (byClient.get(cid) || 0) + share);
          }
        }
        const tid = (en.tag_ids && en.tag_ids[0]) || "__none__";
        byCat.set(tid, (byCat.get(tid) || 0) + sec);
        const aid = en.activity_id || "__none__";
        byAct.set(aid, (byAct.get(aid) || 0) + sec);
      }
      out.set(uid, {
        byClient: toData(byClient, clientMap, "#145c8c"),
        byCategory: toData(byCat, tagMap, "#9333ea"),
        byActivity: toData(byAct, activityMap, "#00b5cc"),
      });
    }
    return out;
  }, [byMember, clientMap, tagMap, activityMap]);

  if (loading && entries.length === 0) {
    return <div className="text-sm text-slate-400 py-12 text-center">Loading team report…</div>;
  }

  const visible = visibleMembers.filter((m) => (byMember.get(m.id)?.entries.length || 0) > 0);
  if (visible.length === 0) {
    return <div className="text-sm text-slate-400 py-12 text-center" data-testid="team-report-empty">
      No tracked time for the selected range.
    </div>;
  }

  return (
    <div className="space-y-4" data-testid="team-report">
      {visible.map((m) => {
        const bucket = byMember.get(m.id);
        const top = topByMember.get(m.id) || [];
        const totalSec = Array.from(bucket.daily.values()).reduce((acc, v) => acc + v.work + v.leave, 0);
        return (
          <div key={m.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden"
               data-testid={`team-report-member-${m.id}`}>
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 bg-slate-50/60">
              <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0"
                   style={{ background: m.color || "#145c8c" }}>
                {(m.name || m.email || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800 truncate">{m.name || m.email}</div>
                <div className="text-[11px] text-slate-500">{m.email}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Range total</div>
                <div className="timer-font text-sm font-semibold text-slate-700">{formatHM(totalSec)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 min-[1100px]:grid-cols-2 gap-0 items-stretch">
              {/* LEFT — 2×2 grid: [Clients pie] [Categories pie] / [Activities pie] [Tracked hours]
                  Each row is 50% of the left column's height (which stretches to
                  match the Top 10 items column on the right). No inner scrollbars. */}
              <div className="p-4 border-r border-slate-100 h-full">
                <div className="grid grid-cols-2 grid-rows-2 gap-x-4 gap-y-4 h-full">
                  <MemberPie title="By Client" data={(distByMember.get(m.id) || {}).byClient || []}
                             testid={`team-pie-client-${m.id}`} />
                  <MemberPie title="By Category" data={(distByMember.get(m.id) || {}).byCategory || []}
                             testid={`team-pie-category-${m.id}`} />
                  <MemberPie title="By Activity" data={(distByMember.get(m.id) || {}).byActivity || []}
                             testid={`team-pie-activity-${m.id}`} />
                  <div data-testid={`team-tracked-hours-${m.id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Tracked hours</div>
                    <table className="w-full text-sm">
                      <tbody>
                        {days.map((d, i) => {
                          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                          const v = bucket.daily.get(key) || { work: 0, leave: 0, leaveType: null };
                          const onLeave = v.leave > 0 && v.work === 0;
                          const tickHit = v.work >= 7 * 3600;
                          return (
                            <tr key={i} className="border-t border-slate-50 first:border-0">
                              <td className="py-1 text-slate-600 text-xs">
                                {d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
                              </td>
                              <td className="py-1 text-right timer-font tabular-nums text-xs"
                                  style={{ color: onLeave ? "#94a3b8" : "#0b2c47", opacity: onLeave ? 0.7 : 1 }}>
                                {onLeave ? `${formatHM(v.leave)} (Leave)` : formatHM(v.work)}
                              </td>
                              <td className="py-1 pl-1.5 w-4">
                                {onLeave ? (
                                  <span className="text-slate-300" title="On leave"
                                        data-testid={`team-daily-leave-${m.id}-${key}`}>—</span>
                                ) : tickHit ? (
                                  <span className="text-emerald-600" title="7+ hours tracked"
                                        data-testid={`team-daily-tick-${m.id}-${key}`}>✓</span>
                                ) : v.work > 0 ? (
                                  <span className="text-amber-500" title="Under 7 hours">·</span>
                                ) : (
                                  <span className="text-slate-300">·</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Top 10 items */}
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Top 10 items</div>
                {top.length === 0 ? (
                  <div className="text-xs text-slate-400 py-2">No work entries in range.</div>
                ) : (
                  <ul className="space-y-1">
                    {top.map((row, i) => {
                      const clients = (row.clientIds || []).map((id) => clientMap[id]).filter(Boolean);
                      const a = row.activityId ? activityMap[row.activityId] : null;
                      const t = row.tagId ? tagMap[row.tagId] : null;
                      return (
                        <li key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0"
                            data-testid={`team-top-${m.id}-${i}`}>
                          <span className="text-xs text-slate-400 w-4 text-right">{i + 1}.</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-slate-800 truncate">{row.description}</div>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap text-[11px]">
                              {clients.map((c) => (
                                <span key={c.id}
                                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium"
                                      style={{ background: `${c.color}1a`, color: c.color }}>{c.name}</span>
                              ))}
                              {t && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-medium"
                                      style={{ background: `${t.color}1a`, color: t.color }}>{t.name}</span>
                              )}
                              {a && (
                                <span className="inline-flex items-center gap-1 text-slate-500">
                                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: a.color }} />
                                  {a.name}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="timer-font tabular-nums text-xs font-semibold text-slate-700 shrink-0">
                            {formatHM(row.seconds)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// Compact per-member distribution pie used inside the Team report tab.
// Renders a small donut (~90px) and a scrollable legend beside it so multiple
// pies fit inside the 2×2 grid without dominating the card.
function MemberPie({ title, data, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">{title}</div>
      {data.length === 0 ? (
        <div className="text-xs text-slate-400 py-4">No data</div>
      ) : (
        <div className="flex items-center gap-3">
          <div style={{ width: 90, height: 90 }} className="shrink-0">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data} dataKey="seconds" nameKey="name"
                     innerRadius={26} outerRadius={42} paddingAngle={2}>
                  {data.map((d, i) => (
                    <Cell key={i} fill={d.color || TEAL_PALETTE[i % TEAL_PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatHM(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 min-w-0">
            {data.map((d, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[11px] py-0.5">
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: d.color }} />
                <span className="truncate flex-1">{d.name}</span>
                <span className="timer-font text-slate-500">{formatHM(d.seconds)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
